import { ErrorPage } from '@common/ErrorPage'
import { LoadingPage } from '@common/LoadingPage'
import { DetailView } from '@components/enitity'
import { withDetailView } from '@components/enitity/detail/Detail.view'
import { SectionHeading } from '@components/section'
import { useUpdateScheduledTask } from '@services/cron/mutations/useUpdateScheduledTask'
import { useScheduledTask } from '@services/cron/queries/useScheduledTask'
import { useScheduledTaskExecutions } from '@services/cron/queries/useScheduledTaskExecutions'
import { CalendarClock, History } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { cn } from '@/lib/utils'

import { statusKey } from '../../status'
import type { JobExecution } from '../../types'
import { CronEditor, type CronFields } from '../../components/CronEditor'

const EMPTY_CRON: CronFields = {
  minute: '0',
  hour: '0',
  day: '*',
  month: '*',
  day_of_week: '*'
}

function statusTone(status: string): string {
  if (status === 'Executed') return 'bg-success-soft text-success'
  if (status === 'Error!' || status === 'Missed!' || status === 'Max instances!')
    return 'bg-destructive/10 text-destructive'
  return 'bg-ink/[0.07] text-ink/60'
}

function ExecutionRow({ execution }: { execution: JobExecution }) {
  const { t } = useTranslation('cron')
  const key = statusKey(execution.status)
  const statusLabel = key ? t(`statuses.${key}`) : execution.status
  return (
    <div className="border-b border-ink/[0.06] py-3 last:border-0">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
        <span
          className={cn(
            'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold',
            statusTone(execution.status)
          )}
        >
          {statusLabel}
        </span>
        <span className="text-ink/70">
          {new Date(execution.run_time).toLocaleString('sk-SK')}
        </span>
        {execution.duration != null && (
          <span className="text-ink/50">{execution.duration}s</span>
        )}
      </div>
      {/* Message-only rows (e.g. "Missed!") have no traceback — show the
          text inline rather than a disclosure that reveals nothing. */}
      {execution.exception && !execution.traceback && (
        <p className="mt-1.5 text-xs text-destructive">
          {execution.exception}
        </p>
      )}
      {execution.traceback && (
        <details className="mt-2">
          <summary className="cursor-pointer text-xs font-medium text-destructive">
            {execution.exception || t('detail.showError')}
          </summary>
          <pre className="mt-2 max-h-80 overflow-auto rounded-lg bg-ink/[0.04] p-3 text-xs text-ink/80">
            {execution.traceback}
          </pre>
        </details>
      )}
    </div>
  )
}

export function CronJobsDetailContent() {
  const { id } = useParams()
  const { t } = useTranslation('cron')
  const { data, isLoading } = useScheduledTask(id)
  const executions = useScheduledTaskExecutions(id)
  const update = useUpdateScheduledTask()

  const [cron, setCron] = useState<CronFields>(EMPTY_CRON)
  const [description, setDescription] = useState('')
  const [enabled, setEnabled] = useState(true)

  useEffect(() => {
    if (!data) return
    setCron({
      minute: data.minute,
      hour: data.hour,
      day: data.day,
      month: data.month,
      day_of_week: data.day_of_week
    })
    setDescription(data.description)
    setEnabled(data.enabled)
  }, [data])

  if (!id) return <ErrorPage />
  if (isLoading) return <LoadingPage />
  if (!data) return <></>

  function save() {
    update.mutate({
      id: data!.id,
      body: { ...cron, description, enabled }
    })
  }

  return (
    <DetailView.Root>
      <DetailView.Breadcrumb
        parentLabel={t('title')}
        parentTo="/admin/cron-jobs"
        current={data.label}
      />

      <DetailView.Content>
        <div className="space-y-8">
          <section>
            <SectionHeading icon={CalendarClock}>
              {t('detail.schedule')}
            </SectionHeading>
            <Card className="space-y-5 p-6">
              <CronEditor value={cron} onChange={setCron} />
              <div className="space-y-1.5">
                <Label>{t('create.description')}</Label>
                <Input
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Switch checked={enabled} onCheckedChange={setEnabled} />
                  <span className="text-sm text-ink/70">
                    {t('columns.enabled')}
                  </span>
                </div>
                <Button disabled={update.isPending} onClick={save}>
                  {t('detail.save')}
                </Button>
              </div>
            </Card>
          </section>

          <section>
            <SectionHeading icon={History}>{t('detail.history')}</SectionHeading>
            <Card className="p-6">
              {executions.isLoading ? (
                <p className="m-0 text-sm text-ink/50">…</p>
              ) : (executions.data ?? []).length === 0 ? (
                <p className="m-0 text-sm text-ink/50">
                  {t('detail.noHistory')}
                </p>
              ) : (
                <div>
                  {(executions.data ?? []).map((execution) => (
                    <ExecutionRow key={execution.id} execution={execution} />
                  ))}
                </div>
              )}
            </Card>
          </section>
        </div>
      </DetailView.Content>
    </DetailView.Root>
  )
}

export const CronJobsDetail = withDetailView(CronJobsDetailContent)
